// Cookie consent (GDPR / ePrivacy): Google Analytics only runs after the visitor
// accepts. The choice is kept for 6 months, then the banner asks again. Rejecting
// stops analytics and deletes the analytics cookies that are already there.
//
// Consent cookie value: "<accepted|rejected>:<time of the choice in ms>". The old
// banner's "accepted" (no time) is not valid consent and is asked again.

export const CONSENT_COOKIE = 'ac-cookie-consent';
export const CONSENT_MAX_AGE_DAYS = 182; // ~6 months
export const GA_MEASUREMENT_ID = 'G-BEDY75X043';

// components listen to this to show the banner again ("Cookie settings")
export const OPEN_SETTINGS_EVENT = 'ac-open-cookie-settings';
export const CONSENT_CHANGE_EVENT = 'ac-consent-change';

const DAY_MS = 24 * 60 * 60 * 1000;
// Google Analytics cookies (_ga, _ga_<id>, _gid, _gat, _gat_<id>) and Google Ads
// conversion linker cookies (_gcl_*)
const TRACKING_COOKIE = /^(_ga|_gid|_gat|_gcl_)/;

const isBrowser = () =>
  typeof window !== 'undefined' && typeof document !== 'undefined';

const readCookie = (name) => {
  if (!isBrowser()) return null;
  const match = document.cookie
    .split(';')
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${name}=`));
  return match ? decodeURIComponent(match.slice(name.length + 1)) : null;
};

// 'accepted' | 'rejected' | null (no choice yet, or the choice expired)
export const getConsent = (now = Date.now()) => {
  const value = readCookie(CONSENT_COOKIE);
  const match = /^(accepted|rejected):(\d+)$/.exec(value || '');
  if (!match) return null;
  const decidedAt = Number(match[2]);
  if (now - decidedAt > CONSENT_MAX_AGE_DAYS * DAY_MS) return null;
  return match[1];
};

// Every domain a cookie for this page may have been set on: GA sets its cookies
// on the top-most domain it can (".aviationchronicles.com"), so deleting needs
// the same domain attribute.
export const cookieDomains = (hostname) => {
  const domains = [''];
  const parts = String(hostname || '').split('.');
  for (let i = 0; i < parts.length - 1; i++) {
    const domain = parts.slice(i).join('.');
    domains.push(domain, `.${domain}`);
  }
  return domains;
};

export const removeTrackingCookies = () => {
  if (!isBrowser()) return [];
  const names = document.cookie
    .split(';')
    .map((part) => part.trim().split('=')[0])
    .filter((name) => TRACKING_COOKIE.test(name));
  const expired = 'expires=Thu, 01 Jan 1970 00:00:00 GMT';
  names.forEach((name) => {
    cookieDomains(window.location.hostname).forEach((domain) => {
      document.cookie = `${name}=; ${expired}; path=/${
        domain ? `; domain=${domain}` : ''
      }`;
    });
  });
  return names;
};

// Browser privacy signals: Global Privacy Control (an opt-out California and other
// US states require sites to honour) and Do Not Track. Either one keeps analytics
// off, even after "Accept all".
export const privacySignalOn = () => {
  if (!isBrowser()) return false;
  return (
    navigator.globalPrivacyControl === true ||
    navigator.doNotTrack === '1' ||
    window.doNotTrack === '1'
  );
};

let analyticsLoaded = false;
const GTAG_SCRIPT_ID = 'ac-gtag';

export const isAnalyticsLoaded = () => analyticsLoaded;

// Loads gtag.js. Only called after the visitor accepted (never on its own).
export const loadAnalytics = () => {
  if (!isBrowser() || analyticsLoaded) return;
  if (privacySignalOn()) return;
  analyticsLoaded = true;
  window[`ga-disable-${GA_MEASUREMENT_ID}`] = false;
  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer.push(arguments); // eslint-disable-line prefer-rest-params
  };
  window.gtag('js', new Date());
  // page views are sent from onRouteUpdate (gatsby-browser.js)
  window.gtag('config', GA_MEASUREMENT_ID, {send_page_view: false});
  if (document.getElementById(GTAG_SCRIPT_ID)) return; // rejected, then accepted again
  const script = document.createElement('script');
  script.id = GTAG_SCRIPT_ID;
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
  document.head.appendChild(script);
};

export const trackPageView = (location) => {
  if (!analyticsLoaded || typeof window.gtag !== 'function') return;
  window.gtag('event', 'page_view', {
    page_path: `${location.pathname}${location.search || ''}`,
    page_location: window.location.href,
    page_title: document.title,
  });
};

export const setConsent = (choice, now = Date.now()) => {
  if (!isBrowser()) return;
  const maxAge = CONSENT_MAX_AGE_DAYS * 24 * 60 * 60;
  document.cookie = `${CONSENT_COOKIE}=${choice}:${now}; max-age=${maxAge}; path=/; SameSite=Lax`;
  if (choice === 'accepted') {
    loadAnalytics();
  } else {
    // stops gtag.js if it was loaded earlier on this page (accepted, then
    // rejected from "Cookie settings")
    window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
    analyticsLoaded = false;
    removeTrackingCookies();
  }
  window.dispatchEvent(new Event(CONSENT_CHANGE_EVENT));
};

export const openCookieSettings = () => {
  if (!isBrowser()) return;
  window.dispatchEvent(new Event(OPEN_SETTINGS_EVENT));
};

// test hook
export const resetAnalyticsForTests = () => {
  analyticsLoaded = false;
};
